import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { policyService } from "@/services/altingross";

// ─── Thunks ─────────────────────────────────────────────────────────────────
export const fetchPolicies = createAsyncThunk(
  "policies/fetchList",
  async ({ page = 1, size = 20, field, value, operator } = {}, { rejectWithValue }) => {
    try {
      const res = await policyService.getList({ page, size, field, value, operator });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const fetchPolicyById = createAsyncThunk(
  "policies/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await policyService.getById(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const createPolicy = createAsyncThunk(
  "policies/create",
  async (data, { rejectWithValue }) => {
    try {
      const res = await policyService.create(data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const updatePolicy = createAsyncThunk(
  "policies/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await policyService.update(id, data);
      return { id, ...res.data };
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const deletePolicy = createAsyncThunk(
  "policies/delete",
  async (id, { rejectWithValue }) => {
    try {
      await policyService.deleteById(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

// ─── Slice ───────────────────────────────────────────────────────────────────
const policiesSlice = createSlice({
  name: "policies",
  initialState: {
    list: [],
    pagination: { page: 1, size: 20, count: 0, totalPages: 1, hasNext: false, hasPrev: false },
    loading: false,
    error: null,
    savingId: null,
    deletingId: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPolicies.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPolicies.fulfilled, (state, action) => {
        state.loading = false;
        const { items, page, size, count, totalPages, hasNext, hasPrev } = action.payload;
        state.list = items ?? [];
        state.pagination = { page, size, count, totalPages, hasNext, hasPrev };
      })
      .addCase(fetchPolicies.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createPolicy.pending, (state) => { state.savingId = "__new__"; })
      .addCase(createPolicy.fulfilled, (state) => { state.savingId = null; })
      .addCase(createPolicy.rejected, (state) => { state.savingId = null; })

      .addCase(updatePolicy.pending, (state, action) => { state.savingId = action.meta.arg.id; })
      .addCase(updatePolicy.fulfilled, (state) => { state.savingId = null; })
      .addCase(updatePolicy.rejected, (state) => { state.savingId = null; })

      .addCase(deletePolicy.pending, (state, action) => { state.deletingId = action.meta.arg; })
      .addCase(deletePolicy.fulfilled, (state, action) => {
        state.deletingId = null;
        state.list = state.list.filter((p) => p.id !== action.payload);
      })
      .addCase(deletePolicy.rejected, (state) => { state.deletingId = null; });
  },
});

export default policiesSlice.reducer;
